import FingerprintJS from "@fingerprintjs/fingerprintjs";
import type { Agent } from "@fingerprintjs/fingerprintjs";

let fpPromise: Promise<Agent> | null = null;

/**
 * Get device fingerprint ID
 * Uses FingerprintJS to generate a unique identifier for the device
 */
export const getDeviceFingerprint = async (): Promise<string> => {
  try {
    // Initialize FingerprintJS only once
    if (!fpPromise) {
      fpPromise = FingerprintJS.load();
    }

    const fp = await fpPromise;
    const result = await fp.get();

    // Store in localStorage for persistence
    const storedFingerprint = localStorage.getItem("deviceFingerprint");
    if (storedFingerprint) {
      return storedFingerprint;
    }

    // Store the visitor ID (fingerprint)
    const fingerprint = result.visitorId;
    localStorage.setItem("deviceFingerprint", fingerprint);
    
    return fingerprint;
  } catch (error) {
    console.error("Error getting device fingerprint:", error);
    
    // Fallback: Use a combination of browser characteristics
    const fallbackFingerprint = generateFallbackFingerprint();
    localStorage.setItem("deviceFingerprint", fallbackFingerprint);
    return fallbackFingerprint;
  }
};

/**
 * Generate a fallback fingerprint using browser characteristics
 * This is used if FingerprintJS fails
 */
const generateFallbackFingerprint = (): string => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("Device fingerprint", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("Device fingerprint", 4, 17);
  }
  
  const canvasFingerprint = canvas.toDataURL();
  
  const deviceMemory = (navigator as any).deviceMemory || "";
  
  const fingerprint = btoa(
    navigator.userAgent +
    navigator.language +
    screen.width +
    screen.height +
    screen.colorDepth +
    new Date().getTimezoneOffset() +
    canvasFingerprint +
    (navigator.hardwareConcurrency || "") +
    deviceMemory
  ).substring(0, 32);

  return fingerprint;
};

/**
 * Check if a device has already voted for a specific project (local storage check only)
 */
export const hasDeviceVoted = async (projectId: string): Promise<boolean> => {
  const votedProjects = JSON.parse(
    localStorage.getItem("votedProjects") || "[]"
  ) as string[];
  return votedProjects.includes(projectId);
};

/**
 * Mark a device as having voted for a project
 */
export const markDeviceAsVoted = async (projectId: string): Promise<void> => {
  const votedProjects = JSON.parse(
    localStorage.getItem("votedProjects") || "[]"
  ) as string[];
  
  if (!votedProjects.includes(projectId)) {
    votedProjects.push(projectId);
    localStorage.setItem("votedProjects", JSON.stringify(votedProjects));
  }
};

/**
 * Clear vote for a specific project (for testing purposes)
 */
export const clearDeviceVote = (projectId: string): void => {
  const votedProjects = JSON.parse(
    localStorage.getItem("votedProjects") || "[]"
  ) as string[];
  
  const filtered = votedProjects.filter((id) => id !== projectId);
  localStorage.setItem("votedProjects", JSON.stringify(filtered));
};

